import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing game time/day-night cycle
 * Cycles through day, dusk, night, and dawn phases
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.cycleDuration - Duration of each phase in milliseconds (default: 45000ms = 45 seconds)
 * @param {string} options.initialTime - Initial time of day (default: 'day')
 * @param {boolean} options.enabled - Whether the clock is running (default: true)
 * @returns {Object} Time state and controls
 */
export const useGameClock = ({
  cycleDuration = 45000,
  initialTime = 'day',
  enabled = true,
} = {}) => {
  const [timeOfDay, setTimeOfDay] = useState(initialTime);
  const [weather, setWeather] = useState('clear');
  const [isPaused, setIsPaused] = useState(!enabled);
  
  // Time cycle sequence
  const TIME_SEQUENCE = ['day', 'day', 'day', 'dusk', 'night', 'night', 'dawn'];
  
  useEffect(() => {
    if (isPaused) return;
    
    let index = TIME_SEQUENCE.indexOf(timeOfDay);
    if (index === -1) index = 0;
    
    const interval = setInterval(() => {
      index = (index + 1) % TIME_SEQUENCE.length;
      setTimeOfDay(TIME_SEQUENCE[index]);
    }, cycleDuration);
    
    return () => clearInterval(interval);
  }, [isPaused, cycleDuration]);
  
  // Weather cycle - random changes every 2 minutes
  useEffect(() => {
    if (isPaused) return;
    const weatherInterval = setInterval(() => {
      const rand = Math.random();
      if (rand < 0.7) setWeather('clear');
      else if (rand < 0.9) setWeather('rain');
      else setWeather('clear');
    }, 120000);
    return () => clearInterval(weatherInterval);
  }, [isPaused]);
  
  // Get the current time-of-day gradient colors
  const getGradientColors = useCallback(() => {
    const gradients = {
      day: 'from-sky-300 via-sky-200 to-emerald-100',
      dawn: 'from-rose-300 via-orange-200 to-yellow-100',
      dusk: 'from-purple-400 via-rose-300 to-orange-200',
      night: 'from-indigo-900 via-slate-800 to-indigo-950',
    };
    return gradients[timeOfDay] || gradients.day;
  }, [timeOfDay]);
  
  // Get lighting conditions for the current time
  const getLightingConditions = useCallback(() => {
    const conditions = {
      day: { brightness: 1, ambientLight: '#ffffff', shadowIntensity: 0.3 },
      dawn: { brightness: 0.8, ambientLight: '#ffd4a3', shadowIntensity: 0.4 },
      dusk: { brightness: 0.7, ambientLight: '#e8a0b3', shadowIntensity: 0.5 },
      night: { brightness: 0.4, ambientLight: '#4a4a6a', shadowIntensity: 0.7 },
    };
    return conditions[timeOfDay] || conditions.day;
  }, [timeOfDay]);
  
  // Check if it's currently night time
  const isNight = timeOfDay === 'night';
  
  // Check if it's currently day time
  const isDay = timeOfDay === 'day';
  
  // Pause the clock
  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);
  
  // Resume the clock
  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);
  
  // Toggle pause state
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);
  
  // Set time to a specific phase
  const setTime = useCallback((time) => {
    if (TIME_SEQUENCE.includes(time)) {
      setTimeOfDay(time);
    }
  }, []);
  
  // Skip to next time phase
  const skipToNext = useCallback(() => {
    const currentIndex = TIME_SEQUENCE.indexOf(timeOfDay);
    const nextIndex = (currentIndex + 1) % TIME_SEQUENCE.length;
    setTimeOfDay(TIME_SEQUENCE[nextIndex]);
  }, [timeOfDay]);
  
  return {
    timeOfDay,
    weather,
    isPaused,
    isNight,
    isDay,
    getGradientColors,
    getLightingConditions,
    pause,
    resume,
    togglePause,
    setTime,
    setWeather,
    skipToNext,
  };
};

export default useGameClock;
